import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription } from 'rxjs';

import { AccessControlService, AccessNavItem, AppUserService, SubscriptionService, SubscriptionUsageSummary } from '../core/services';
import { TranslatePipe } from '../core/pipes/translate.pipe';
import { LanguageSwitchComponent } from '../core/ui/language-switch/language-switch.component';

@Component({
  selector: 'app-nav',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TranslatePipe, LanguageSwitchComponent],
  templateUrl: './nav.html',
  styleUrl: './nav.css',
})
export class Nav implements OnInit, OnDestroy {
  private readonly helpNavItem: AccessNavItem = {
    id: -100,
    label: 'Help',
    path: 'help-user-manual',
    service: 'HELP_USER_MANUAL',
    menuPosition: 10000
  };

  isLoading = false;
  isCollapsed = false;
  isMobileView = false;
  isMobileMenuOpen = false;
  navItems: AccessNavItem[] = [];
  currentRole: number | null = null;
  usageSummary: SubscriptionUsageSummary | null = null;
  private readonly routerSubscription: Subscription;
  private summaryRefreshSubscription?: Subscription;

  constructor(
    private readonly access: AccessControlService,
    private readonly router: Router,
    private readonly appUsers: AppUserService,
    private readonly subscriptionService: SubscriptionService
  ) {
    const stored = localStorage.getItem('htally_nav_collapsed');
    this.isCollapsed = stored === 'true';
    this.updateViewportState();
    this.applySidebarState();
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && this.isMobileView) {
        this.closeMobileMenu();
      }
    });
  }

  ngOnInit(): void {
    this.access.ensureLoaded().subscribe({
      next: () => {
        this.navItems = this.withHelpNavItem(this.access.navItems);
      },
      error: () => {
        this.navItems = [];
      }
    });

    this.appUsers.me().subscribe({
      next: (profile) => {
        this.currentRole = Number(profile?.role ?? 0) || null;
        if (this.currentRole === 4) {
          this.usageSummary = null;
          return;
        }
        this.loadUsageSummary();
        this.summaryRefreshSubscription?.unsubscribe();
        this.summaryRefreshSubscription = this.subscriptionService.summaryRefresh$.subscribe(() => {
          this.loadUsageSummary();
        });
      },
      error: () => {
        this.currentRole = null;
        this.usageSummary = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.summaryRefreshSubscription?.unsubscribe();
  }

  onButtonClick(): void {
    this.isLoading = true;
    console.log('Navigating to renter list...');
    setTimeout(() => {
      this.isLoading = false;
    }, 1000);
  }

  toggleSidebar(): void {
    if (this.isMobileView) {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      this.applySidebarState();
      return;
    }
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem('htally_nav_collapsed', String(this.isCollapsed));
    this.applySidebarState();
  }

  closeMobileMenu(): void {
    if (!this.isMobileView || !this.isMobileMenuOpen) {
      return;
    }
    this.isMobileMenuOpen = false;
    this.applySidebarState();
  }

  trackByMenuId(_index: number, item: AccessNavItem): number {
    return item.id;
  }

  get showStorageUsage(): boolean {
    return this.currentRole !== 4 && !!this.usageSummary;
  }

  get storageUsagePercent(): number {
    const used = Number(this.usageSummary?.usage?.usedFileSize ?? 0);
    const total = this.totalStorageBytes;
    if (total <= 0) {
      return 0;
    }
    return Math.min(100, Math.max(0, (used / total) * 100));
  }

  get totalStorageBytes(): number {
    const totalMb = Number(this.usageSummary?.currentPackage?.maxFileStorage ?? 0);
    if (!Number.isFinite(totalMb) || totalMb <= 0) {
      return 0;
    }
    return totalMb * 1024 * 1024;
  }

  get storageUsagePercentLabel(): string {
    return `${Math.round(this.storageUsagePercent)}%`;
  }

  get packageExpireDateLabel(): string | null {
    const rawDate = `${this.usageSummary?.currentPackage?.expireDate ?? ''}`.trim();
    if (!rawDate) {
      return null;
    }
    const [yearText, monthText, dayText] = rawDate.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return rawDate;
    }
    const parsed = new Date(year, month - 1, day);
    if (Number.isNaN(parsed.getTime())) {
      return rawDate;
    }
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(parsed);
  }

  formatStorage(sizeInBytes: number | null | undefined): string {
    const size = Number(sizeInBytes ?? 0);
    if (!Number.isFinite(size) || size <= 0) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = size;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const digits = value >= 100 || unitIndex === 0 ? 0 : value >= 10 ? 1 : 2;
    return `${value.toFixed(digits)} ${units[unitIndex]}`;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportState();
    this.applySidebarState();
  }

  private updateViewportState(): void {
    this.isMobileView = typeof window !== 'undefined' && window.innerWidth <= 900;
    if (!this.isMobileView) {
      this.isMobileMenuOpen = false;
    }
  }

  private applySidebarState(): void {
    const sidebarWidth = this.isMobileView
      ? '0px'
      : (this.isCollapsed ? '92px' : '248px');
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);

    if (this.isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }

    if (this.isMobileView && this.isMobileMenuOpen) {
      document.body.classList.add('mobile-nav-open');
    } else {
      document.body.classList.remove('mobile-nav-open');
    }
  }

  private loadUsageSummary(): void {
    this.subscriptionService.getSubscriptionSummary().subscribe({
      next: (summary) => {
        this.usageSummary = summary;
      },
      error: () => {
        this.usageSummary = null;
      }
    });
  }

  private withHelpNavItem(items: AccessNavItem[]): AccessNavItem[] {
    const hasHelpItem = items.some((item) => item.service === this.helpNavItem.service || item.path === this.helpNavItem.path);
    return hasHelpItem ? items : [...items, this.helpNavItem];
  }
}
